import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Building2, MapPin, Award, Users as UsersIcon, Save, ShieldCheck, Edit } from "lucide-react";

export default function Organization() {
  const { user, refreshSettings } = useAuth();
  const isSA = user?.role === "super_admin";
  const isHR = user?.role === "hr_admin";
  const canEdit = isSA || isHR;

  const [profile, setProfile] = useState(null);
  const [depts, setDepts] = useState([]);
  const [locs, setLocs] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [resps, setResps] = useState([]);
  const [emps, setEmps] = useState([]);

  const load = async () => {
    const [p, d, l, ds, rs, e] = await Promise.all([
      api.get("/organization/profile"),
      api.get("/departments"),
      api.get("/locations"),
      api.get("/designations"),
      api.get("/responsibilities"),
      api.get("/employees"),
    ]);
    setProfile(p.data); setDepts(d.data); setLocs(l.data); setDesigs(ds.data); setResps(rs.data); setEmps(e.data);
  };
  useEffect(() => { load(); }, []);

  if (!profile) return <div className="p-8 text-slate-500">Loading organization…</div>;

  const saveProfile = async () => {
    try {
      await api.patch("/organization/profile", profile);
      await refreshSettings();
      toast.success("Company profile updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="organization-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Administration</div>
        <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Organization</h1>
        <p className="text-sm text-slate-600 mt-1">Company profile, branches, departments, designations</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="profile"><Building2 className="w-3.5 h-3.5 mr-1.5"/>Company Profile</TabsTrigger>
          <TabsTrigger value="departments"><UsersIcon className="w-3.5 h-3.5 mr-1.5"/>Departments ({depts.length})</TabsTrigger>
          <TabsTrigger value="locations"><MapPin className="w-3.5 h-3.5 mr-1.5"/>Branches ({locs.length})</TabsTrigger>
          <TabsTrigger value="designations"><Award className="w-3.5 h-3.5 mr-1.5"/>Designations ({desigs.length})</TabsTrigger>
          <TabsTrigger value="responsibilities"><ShieldCheck className="w-3.5 h-3.5 mr-1.5"/>Responsibilities ({resps.length})</TabsTrigger>
        </TabsList>

        {/* COMPANY PROFILE */}
        <TabsContent value="profile" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-none space-y-5">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Legal & Identity</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Legal Name</Label><Input value={profile.legal_name || ""} onChange={(e) => setProfile({...profile, legal_name: e.target.value})} disabled={!canEdit} className="mt-1" data-testid="org-legal-name"/></div>
              <div><Label>Display Name</Label><Input value={profile.display_name || ""} onChange={(e) => setProfile({...profile, display_name: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
              <div><Label>GST / Tax ID</Label><Input value={profile.gst || ""} onChange={(e) => setProfile({...profile, gst: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
              <div><Label>Company Registration (CIN)</Label><Input value={profile.cin || ""} onChange={(e) => setProfile({...profile, cin: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
            </div>

            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 pt-3">Contact</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Label>Registered Address</Label><Textarea value={profile.registered_address || ""} onChange={(e) => setProfile({...profile, registered_address: e.target.value})} disabled={!canEdit} className="mt-1" rows={2}/></div>
              <div><Label>Phone</Label><Input value={profile.phone || ""} onChange={(e) => setProfile({...profile, phone: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
              <div><Label>Support Email</Label><Input value={profile.support_email || ""} onChange={(e) => setProfile({...profile, support_email: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
              <div><Label>Website</Label><Input value={profile.website || ""} onChange={(e) => setProfile({...profile, website: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
            </div>

            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 pt-3">Operations</div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Timezone</Label><Input value={profile.timezone || ""} onChange={(e) => setProfile({...profile, timezone: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
              <div><Label>Currency</Label><Input value={profile.currency || ""} onChange={(e) => setProfile({...profile, currency: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
              <div><Label>Fiscal Year Start (MM-DD)</Label><Input value={profile.fiscal_year_start || ""} onChange={(e) => setProfile({...profile, fiscal_year_start: e.target.value})} disabled={!canEdit} className="mt-1"/></div>
            </div>

            {isSA && (
              <>
                <div className="text-xs uppercase tracking-[0.15em] font-semibold text-cyan-700 pt-3">Branding (Super Admin only)</div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div><Label>Software Name</Label><Input value={profile.software_name || ""} onChange={(e) => setProfile({...profile, software_name: e.target.value})} className="mt-1" data-testid="org-software-name"/></div>
                  <div className="md:col-span-2"><Label>Tagline</Label><Input value={profile.tagline || ""} onChange={(e) => setProfile({...profile, tagline: e.target.value})} className="mt-1"/></div>
                  <div><Label>Logo Initial</Label><Input maxLength={2} value={profile.logo_initial || ""} onChange={(e) => setProfile({...profile, logo_initial: e.target.value.toUpperCase()})} className="mt-1 w-24"/></div>
                  <div><Label>Primary</Label><Input type="color" value={profile.primary_color || "#0F172A"} onChange={(e) => setProfile({...profile, primary_color: e.target.value})} className="mt-1 h-11"/></div>
                  <div><Label>Accent</Label><Input type="color" value={profile.accent_color || "#06B6D4"} onChange={(e) => setProfile({...profile, accent_color: e.target.value})} className="mt-1 h-11"/></div>
                </div>
              </>
            )}

            {canEdit ? (
              <Button onClick={saveProfile} className="bg-slate-900 hover:bg-slate-800" data-testid="org-save"><Save className="w-4 h-4 mr-1"/>Save Changes</Button>
            ) : (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">Only Super Admin and HR Admin can edit company profile.</div>
            )}
          </Card>
        </TabsContent>

        {/* DEPARTMENTS */}
        <TabsContent value="departments" className="mt-4">
          <DeptManager depts={depts} emps={emps} canEdit={canEdit} onChange={load} />
        </TabsContent>

        {/* LOCATIONS */}
        <TabsContent value="locations" className="mt-4">
          <LocManager locs={locs} canEdit={canEdit} onChange={load} />
        </TabsContent>

        {/* DESIGNATIONS */}
        <TabsContent value="designations" className="mt-4">
          <DesigManager desigs={desigs} canEdit={canEdit} onChange={load} />
        </TabsContent>

        {/* RESPONSIBILITIES */}
        <TabsContent value="responsibilities" className="mt-4">
          <RespManager resps={resps} canEdit={canEdit} onChange={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DeptManager({ depts, emps, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", head_id: "" });
  const create = async () => {
    try { await api.post("/departments", { ...form, head_id: form.head_id || null }); toast.success("Department added"); setOpen(false); setForm({name:"",code:"",head_id:""}); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    try { await api.patch(`/departments/${editDept.id}`, { name: editDept.name, code: editDept.code || "", head_id: editDept.head_id || null }); toast.success("Department updated"); setEditDept(null); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try { await api.delete(`/departments/${id}`); toast.success("Deleted"); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <Card className="border-slate-200 shadow-none">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900">Departments</div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="new-dept-btn"><Plus className="w-4 h-4 mr-1"/>New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Department</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1" data-testid="dept-new-name"/></div>
                <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} className="mt-1"/></div>
                <div>
                  <Label>Department Head</Label>
                  <Select value={form.head_id} onValueChange={(v) => setForm({...form, head_id: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select head"/></SelectTrigger>
                    <SelectContent>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-slate-900 hover:bg-slate-800">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {depts.map((d) => {
          const head = emps.find(e => e.id === d.head_id);
          return (
            <div key={d.id} className="p-4 flex items-center gap-4" data-testid={`dept-row-${d.id}`}>
              <div className="flex-1">
                <div className="font-medium text-slate-900">{d.name} <span className="text-xs text-slate-400 ml-2 uppercase tracking-wider">{d.code}</span></div>
                <div className="text-xs text-slate-500 mt-0.5">{d.employee_count || 0} employees · Head: {head?.full_name || "—"}</div>
              </div>
              {canEdit && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setEditDept({ ...d })} className="text-slate-600" data-testid={`dept-edit-${d.id}`}><Edit className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(d.id)} className="text-red-600" data-testid={`dept-delete-${d.id}`}><Trash2 className="w-4 h-4"/></Button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editDept} onOpenChange={(v) => !v && setEditDept(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Department</DialogTitle></DialogHeader>
          {editDept && (
            <div className="space-y-3 py-2">
              <div><Label>Name</Label><Input value={editDept.name} onChange={(e) => setEditDept({...editDept, name: e.target.value})} className="mt-1" data-testid="dept-edit-name"/></div>
              <div><Label>Code</Label><Input value={editDept.code || ""} onChange={(e) => setEditDept({...editDept, code: e.target.value})} className="mt-1"/></div>
              <div>
                <Label>Department Head</Label>
                <Select value={editDept.head_id || ""} onValueChange={(v) => setEditDept({...editDept, head_id: v})}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select head"/></SelectTrigger>
                  <SelectContent>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800" data-testid="dept-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function LocManager({ locs, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", city: "", address: "", country: "India", timezone: "Asia/Kolkata" });
  const create = async () => {
    try { await api.post("/locations", form); toast.success("Branch added"); setOpen(false); setForm({name:"",code:"",city:"",address:"",country:"India",timezone:"Asia/Kolkata"}); onChange(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try { await api.delete(`/locations/${id}`); toast.success("Deleted"); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <Card className="border-slate-200 shadow-none">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900">Branches / Locations</div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1"/>New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Branch</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1"/></div>
                  <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} className="mt-1"/></div>
                </div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="mt-1"/></div>
                <div><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="mt-1" rows={2}/></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-slate-900 hover:bg-slate-800">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {locs.map((l) => (
          <div key={l.id} className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-md bg-slate-100"><MapPin className="w-4 h-4 text-slate-600"/></div>
            <div className="flex-1">
              <div className="font-medium text-slate-900">{l.name} <span className="text-xs text-slate-400 ml-2 uppercase tracking-wider">{l.code}</span></div>
              <div className="text-xs text-slate-500 mt-0.5">{l.city}, {l.country} · {l.timezone}</div>
              {l.address && <div className="text-xs text-slate-400 mt-0.5">{l.address}</div>}
            </div>
            {canEdit && <Button variant="ghost" size="sm" onClick={() => del(l.id)} className="text-red-600"><Trash2 className="w-4 h-4"/></Button>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function DesigManager({ desigs, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", level: "" });
  const create = async () => {
    try { await api.post("/designations", form); toast.success("Designation added"); setOpen(false); setForm({title:"",level:""}); onChange(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/designations/${id}`); toast.success("Deleted"); onChange(); } catch { toast.error("Failed"); }
  };
  return (
    <Card className="border-slate-200 shadow-none">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900">Designations / Job Titles</div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1"/>New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Designation</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1"/></div>
                <div><Label>Level (e.g. L3, L5)</Label><Input value={form.level} onChange={(e) => setForm({...form, level: e.target.value})} className="mt-1"/></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-slate-900 hover:bg-slate-800">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 p-3">
        {desigs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 p-3 rounded-md border border-slate-200 bg-slate-50">
            <Award className="w-4 h-4 text-slate-500"/>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">{d.title}</div>
              {d.level && <div className="text-xs text-slate-500">{d.level}</div>}
            </div>
            {canEdit && <Button variant="ghost" size="sm" onClick={() => del(d.id)} className="text-red-600 h-7"><Trash2 className="w-3.5 h-3.5"/></Button>}
          </div>
        ))}
      </div>
    </Card>
  );
}


function RespManager({ resps, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "general", description: "" });
  const create = async () => {
    try { await api.post("/responsibilities", form); toast.success("Added"); setOpen(false); setForm({name:"",category:"general",description:""}); onChange(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this responsibility?")) return;
    try { await api.delete(`/responsibilities/${id}`); toast.success("Deleted"); onChange(); } catch { toast.error("Failed"); }
  };
  const byCat = {};
  resps.forEach((r) => { (byCat[r.category || "general"] ||= []).push(r); });
  return (
    <Card className="border-slate-200 shadow-none">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900">Functional Responsibilities</div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1"/>New</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Responsibility</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1"/></div>
                <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="mt-1" placeholder="hr / approvals / finance / work / assets / lifecycle"/></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1" rows={2}/></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-slate-900 hover:bg-slate-800">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="p-3 space-y-3">
        {Object.entries(byCat).map(([cat, items]) => (
          <div key={cat}>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1.5 ml-1">{cat}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {items.map((r) => (
                <div key={r.id} className="flex items-start gap-3 p-3 rounded-md border border-slate-200 bg-slate-50">
                  <ShieldCheck className="w-4 h-4 text-cyan-600 mt-0.5 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{r.name}</div>
                    {r.description && <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>}
                  </div>
                  {canEdit && <Button variant="ghost" size="sm" onClick={() => del(r.id)} className="text-red-600 h-7"><Trash2 className="w-3.5 h-3.5"/></Button>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
